// @mui material components
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";

// Images
import mort from "assets/images/Performance_grey.png";
import sw from "assets/images/water_pipe.png";
import sc from "assets/images/Temp_grey.png";
//import lux from "assets/images/Light_grey.png";
import act from "assets/images/Key_activities_grey.png";

import PropTypes from "prop-types";
import colors from "assets/theme/base/colors";

const Parametre = ({ image, name, onClick }) => {
  const clickable = typeof onClick === "function";
  return (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDTypography
        variant="button"
        fontWeight="medium"
        ml={1}
        lineHeight={1}
        component={clickable ? "button" : "span"}
        type={clickable ? "button" : undefined}
        onClick={onClick}
        sx={
          clickable
            ? {
                color: colors.info.main,
                textDecoration: "underline",
                cursor: "pointer",
                background: "none",
                border: "none",
                p: 0,
              }
            : undefined
        }
      >
        {name}
      </MDTypography>
    </MDBox>
  );
};

Parametre.propTypes = {
  image: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const hasMetricValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "loading..." ||
    value === "null"
  ) {
    return false;
  }
  return Number.isFinite(Number(value));
};

const renderValue = (value, status, statusToColor) => {
  const hasValue = hasMetricValue(value);
  const numericValue = Number(value);

  return (
    <MDTypography
      variant="h6"
      color={hasValue ? statusToColor[status] || "light" : "text"}
      fontWeight="medium"
    >
      {hasValue ? `${numericValue}%` : "--%"}
    </MDTypography>
  );
};

const renderFailedValue = () => (
  <MDTypography variant="h6" color="error" fontWeight="medium">
    Failed
  </MDTypography>
);

const renderMessageValue = (message) => (
  <MDTypography
    sx={{
      fontSize: "0.9rem",
      color: "text.light",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: "200px",
    }}
  >
    {message}
  </MDTypography>
);

const renderProgress = (value, status, statusToColor) => (
  <MDBox width="8rem" textAlign="left">
    <MDProgress
      value={hasMetricValue(value) ? Number(value) : 0}
      color={hasMetricValue(value) ? statusToColor[status] || "light" : "light"}
      variant="gradient"
      label={false}
    />
  </MDBox>
);

const renderAvgCell = ({ value, status, error = false, message = null, statusToColor }) => {
  if (error) return renderFailedValue();
  if (message) return renderMessageValue(message);
  return renderValue(value, status, statusToColor);
};

const renderStatusCell = ({ value, status, error = false, message = null, statusToColor }) => {
  if (error || message || !hasMetricValue(value)) return null;
  return renderProgress(value, status, statusToColor);
};

export default function data({
  avg_temp_dp,
  avg_tempdp_status,
  avg_tempdp_error,
  avg_hum_dp,
  avg_humdp_status,
  avg_humdp_error,
  avg_ftemp_dp,
  avg_ftempdp_status,
  avg_ftempdp_error,
  avg_nh3_dp,
  avg_nh3dp_status,
  avg_nh3dp_error,
  avg_fcr_dp,
  avg_fcrdp_status,
  avg_fcrdp_error,
  avg_mortality_dp,
  avg_mortalitydp_status,
  avg_mortalitydp_error,
  avg_weight_dp,
  avg_weightdp_status,
  avg_weightdp_message,
  avg_weightdp_error,
  avg_wtemp_dp,
  avg_wtempdp_status,
  avg_wtempdp_error,
  avg_lux_dp,
  avg_luxdp_status,
  avg_luxdp_error,
  avg_dark_dp,
  avg_darkdp_status,
  avg_darkdp_error,
  avg_ph_dp,
  avg_phdp_status,
  avg_act_dp,
  avg_actdp_status,
  avg_actdp_error,
  onMortalityClick,
}) {
  const statusToColor = {
    Red: "error",
    Orange: "warning",
    Amber: "warning",
    Green: "success",
    Yellow: "warning",
    Neutral: "info",
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="body1" fontWeight="medium" fontSize="0.8rem" color="grey">
          PARAMETRE
        </MDTypography>
      ),
      accessor: "parametre",
      width: "20%",
      align: "LEFT",
    },
    {
      Header: (
        <MDTypography variant="body1" fontWeight="medium" fontSize="0.8rem" color="grey">
          AVG TARGET DEVIATION
        </MDTypography>
      ),
      accessor: "avg_target_deviation",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="body1" fontWeight="medium" fontSize="0.8rem" color="grey">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
    },
  ];

  const rows = [
    {
      parametre: <Parametre image={mort} name="FCR" />,
      avg_target_deviation: renderAvgCell({
        value: avg_fcr_dp,
        status: avg_fcrdp_status,
        error: avg_fcrdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_fcr_dp,
        status: avg_fcrdp_status,
        error: avg_fcrdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={mort} name="Weight" />,
      avg_target_deviation: renderAvgCell({
        value: avg_weight_dp,
        status: avg_weightdp_status,
        error: avg_weightdp_error,
        message: avg_weightdp_message,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_weight_dp,
        status: avg_weightdp_status,
        error: avg_weightdp_error,
        message: avg_weightdp_message,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={mort} name="Mortality" onClick={onMortalityClick} />,
      avg_target_deviation: renderAvgCell({
        value: avg_mortality_dp,
        status: avg_mortalitydp_status,
        error: avg_mortalitydp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_mortality_dp,
        status: avg_mortalitydp_status,
        error: avg_mortalitydp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="Ambient Temp" />,
      avg_target_deviation: renderAvgCell({
        value: avg_temp_dp,
        status: avg_tempdp_status,
        error: avg_tempdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_temp_dp,
        status: avg_tempdp_status,
        error: avg_tempdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="Humidity" />,
      avg_target_deviation: renderAvgCell({
        value: avg_hum_dp,
        status: avg_humdp_status,
        error: avg_humdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_hum_dp,
        status: avg_humdp_status,
        error: avg_humdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="Floor Temp" />,
      avg_target_deviation: renderAvgCell({
        value: avg_ftemp_dp,
        status: avg_ftempdp_status,
        error: avg_ftempdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_ftemp_dp,
        status: avg_ftempdp_status,
        error: avg_ftempdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="NH3" />,
      avg_target_deviation: renderAvgCell({
        value: avg_nh3_dp,
        status: avg_nh3dp_status,
        error: avg_nh3dp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_nh3_dp,
        status: avg_nh3dp_status,
        error: avg_nh3dp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="Lighting" />,
      avg_target_deviation: renderAvgCell({
        value: avg_lux_dp,
        status: avg_luxdp_status,
        error: avg_luxdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_lux_dp,
        status: avg_luxdp_status,
        error: avg_luxdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sc} name="Dark period" />,
      avg_target_deviation: renderAvgCell({
        value: avg_dark_dp,
        status: avg_darkdp_status,
        error: avg_darkdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_dark_dp,
        status: avg_darkdp_status,
        error: avg_darkdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={sw} name="Water Temp" />,
      avg_target_deviation: renderAvgCell({
        value: avg_wtemp_dp,
        status: avg_wtempdp_status,
        error: avg_wtempdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_wtemp_dp,
        status: avg_wtempdp_status,
        error: avg_wtempdp_error,
        statusToColor,
      }),
    },
    {
      parametre: <Parametre image={act} name="Activities" />,
      avg_target_deviation: renderAvgCell({
        value: avg_act_dp,
        status: avg_actdp_status,
        error: avg_actdp_error,
        statusToColor,
      }),
      status: renderStatusCell({
        value: avg_act_dp,
        status: avg_actdp_status,
        error: avg_actdp_error,
        statusToColor,
      }),
    },
  ];

  return { columns, rows };
}
